import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ArtifactType } from '../../store/types';
import TitleEditor from '../title-editor';
import ReportBuilder from './report-builder';
import ReportFileInstructions from './report-file-instructions';
import { outlineOf } from './report-outline';
import ReportPreviewButton from './report-preview-button';
type ReportDocumentProps = {
  name: string;
  spec: string;
  type: ArtifactType;
  editable: boolean;
  byline?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  onName: (name: string) => void;
  /** `save` is set for an edit that commits from a portal or an unmounting control, which no blur follows. */
  onSpec: (spec: string, options: {
    save: boolean;
  }) => void;
};
const ReportDocument = t0 => {
  const $ = _c(55);
  const {
    name,
    spec,
    type,
    editable,
    byline,
    actions,
    children,
    onName,
    onSpec
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "agentBuilder.canvas.report"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("agents", t1);
  let t2;
  if ($[1] !== spec || $[2] !== type) {
    t2 = type === "markdown" ? outlineOf(spec) : [];
    $[1] = spec;
    $[2] = type;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const outline = t2;
  let t3;
  if ($[4] !== t) {
    t3 = t(_temp);
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t(_temp2);
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== editable || $[9] !== name || $[10] !== onName || $[11] !== t3 || $[12] !== t4) {
    t5 = <div className="min-w-0 flex-1"><TitleEditor value={name} placeholder={t3} ariaLabel={t4} editable={editable} size="section" onChange={onName} /></div>;
    $[8] = editable;
    $[9] = name;
    $[10] = onName;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== name || $[15] !== outline) {
    t6 = outline.length > 0 && <ReportPreviewButton name={name} sections={outline} />;
    $[14] = name;
    $[15] = outline;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== t || $[18] !== type) {
    t7 = type !== "markdown" && <span className="text-body-3 text-tertiary">{t(_temp3, {
        format: type.toUpperCase()
      })}</span>;
    $[17] = t;
    $[18] = type;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== actions || $[21] !== t6 || $[22] !== t7) {
    t8 = <div className="flex shrink-0 items-center gap-2">{t6}{t7}{actions}</div>;
    $[20] = actions;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t5 || $[25] !== t8) {
    t9 = <div className="flex items-center gap-3">{t5}{t8}</div>;
    $[24] = t5;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== byline) {
    t10 = byline && <p className="mt-1 text-body-2 text-tertiary">{byline}</p>;
    $[27] = byline;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t9) {
    t11 = <div>{t9}{t10}</div>;
    $[29] = t10;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const header = t11;
  if (type !== "markdown") {
    let t12;
    if ($[32] !== onSpec) {
      t12 = next => onSpec(next, {
        save: false
      });
      $[32] = onSpec;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    let t13;
    if ($[34] !== editable || $[35] !== spec || $[36] !== t12 || $[37] !== type) {
      t13 = <ReportFileInstructions format={type} instructions={spec} editable={editable} onChange={t12} />;
      $[34] = editable;
      $[35] = spec;
      $[36] = t12;
      $[37] = type;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    let t14;
    if ($[39] !== children || $[40] !== header || $[41] !== t13) {
      t14 = <div className="flex flex-col gap-6">{header}{t13}{children}</div>;
      $[39] = children;
      $[40] = header;
      $[41] = t13;
      $[42] = t14;
    } else {
      t14 = $[42];
    }
    return t14;
  }
  let t12;
  if ($[43] !== onSpec) {
    t12 = (next_0, t13) => {
      const {
        structural
      } = t13;
      return onSpec(next_0, {
        save: structural
      });
    };
    $[43] = onSpec;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== editable || $[46] !== spec || $[47] !== t12) {
    t13 = <ReportBuilder editable={editable} onChange={t12} spec={spec} />;
    $[45] = editable;
    $[46] = spec;
    $[47] = t12;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] !== header || $[50] !== t13) {
    t14 = <div className="flex flex-col gap-5">{header}{t13}</div>;
    $[49] = header;
    $[50] = t13;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== children || $[53] !== t14) {
    t15 = <div>{t14}{children}</div>;
    $[52] = children;
    $[53] = t14;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  return t15;
};
export default ReportDocument;
function _temp($) {
  return $.namePlaceholder;
}
function _temp2($_0) {
  return $_0.nameLabel;
}
function _temp3($_1) {
  return $_1.fileLabel;
}